"use client";

import { SiteTemplatePageSectionTypeWhyDefaultListItemSettingModel } from "@/Apis/ClinicApi";
import { Html } from "@/Components";
import { CenterOptions } from "@/Helpers/CenterOptions";
import { WhySectionProps } from "@/Templates/SectionsProps";
import { FC, ReactElement, ReactNode } from "react";
import HomeEighteenWhyChooseSectionGenerated, {
  HomeEighteenWhyChooseSectionGeneratedProps,
} from "../Sections/_generated/HomeEighteenWhyChooseSection.generated";
import HomeNineWhyChooseSectionGenerated, {
  HomeNineWhyChooseSectionGeneratedProps,
} from "../Sections/_generated/HomeNineWhyChooseSection.generated";
import HomeSixWhySectionGenerated, {
  HomeSixWhySectionGeneratedProps,
} from "../Sections/_generated/HomeSixWhySection.generated";
import {
  buildGeneratedSectionProps,
  renderGeneratedSection,
} from "./generated-section-renderer";

type GeneratedWhyRenderer = (props: WhySectionProps) => ReactElement;

const WhyAppearance =
  CenterOptions.SiteTemplate.SiteTemplateSettings.SiteTemplateWhyAppearance;

function renderHtmlValue(value?: string | null): ReactNode {
  if (!value) {
    return undefined;
  }

  return <Html value={value} />;
}

function getListItems(
  props: WhySectionProps,
): SiteTemplatePageSectionTypeWhyDefaultListItemSettingModel[] {
  return props.lists.Default ?? [];
}

function getSectionImageUrl(props: WhySectionProps): string | undefined {
  return props.setting.Image?.Url ?? undefined;
}

function getSectionImageAlt(props: WhySectionProps, fallback: string): string {
  return props.setting.Image?.AltText ?? fallback;
}

function getItemImageUrl(
  item?: SiteTemplatePageSectionTypeWhyDefaultListItemSettingModel | null,
): string | undefined {
  return item?.Image?.Url ?? undefined;
}

function getItemImageAlt(
  item: SiteTemplatePageSectionTypeWhyDefaultListItemSettingModel | undefined,
  fallback: string,
): string {
  return item?.Image?.AltText ?? item?.Title ?? fallback;
}

function getDefaultStepText(index: number): string {
  return `0${index + 1}`;
}

const generatedWhyRenderers: Record<string, GeneratedWhyRenderer> = {
  [WhyAppearance.Home6.key]: (props) => {
    const items = getListItems(props);

    return (
      <HomeSixWhySectionGenerated
        {...buildGeneratedSectionProps<HomeSixWhySectionGeneratedProps>(
          "HomeSixWhySection",
          {
            sectionDiv0Div0Div0Div0Div0DivText:
              props.setting.Subtitle ?? undefined,
            sectionDiv0Div0Div0Div0H20H2Text: props.setting.Title ?? undefined,
            sectionDiv0Div0Div0Div0H20Span0SpanText:
              props.setting.Description ?? undefined,
            sectionDiv0Div0Div0Div0P0PText: renderHtmlValue(
              props.setting.Description,
            ),
            sectionDiv0Div0Div1Div0ImageWithBasePath0ImageSrc:
              getSectionImageUrl(props) ?? undefined,
            sectionDiv0Div0Div1Div0ImageWithBasePath0ImageAlt:
              getSectionImageAlt(props, "why"),
            sectionDiv0Div0Div1Div0ImageWithBasePath1ImageSrc:
              getItemImageUrl(items[0]) ?? undefined,
            sectionDiv0Div0Div1Div0ImageWithBasePath1ImageAlt: getItemImageAlt(
              items[0],
              "why",
            ),
            sectionImageWithBasePath0ImageSrc:
              getItemImageUrl(items[1]) ?? undefined,
            sectionImageWithBasePath0ImageAlt: getItemImageAlt(items[1], "why"),
            whySectionChooseSixs: items.map((item, index) => ({
              key: item.Id,
              className:
                index === items.length - 1
                  ? "choose-item-six mb-0"
                  : "choose-item-six",
              span0Text: item.Subtitle ?? getDefaultStepText(index),
              h30Text: item.Title ?? "",
              p0Text: renderHtmlValue(item.Description),
            })),
          },
        )}
      />
    );
  },
  [WhyAppearance.Home9.key]: (props) => {
    const items = getListItems(props);
    const leftItems = items.slice(0, 2);
    const rightItems = items.slice(2, 4);
    const stepItems = items.slice(4);

    return (
      <HomeNineWhyChooseSectionGenerated
        {...buildGeneratedSectionProps<HomeNineWhyChooseSectionGeneratedProps>(
          "HomeNineWhyChooseSection",
          {
            sectionDiv0Div0Div0DivText: props.setting.Subtitle ?? undefined,
            sectionDiv0Div0H20H2Text: props.setting.Title ?? undefined,
            sectionDiv0Div0H20Span0SpanText:
              props.setting.Description ?? undefined,
            sectionDiv0Div0H20H2Text2: "",
            sectionDiv0Div1Div1Div0ImageWithBasePath0ImageSrc:
              getSectionImageUrl(props) ?? undefined,
            sectionDiv0Div1Div1Div0ImageWithBasePath0ImageAlt:
              getSectionImageAlt(props, "why"),
            whyChooseSectionChooseNineFadeInUps: leftItems.map(
              (item, index) => ({
                key: item.Id,
                dataWowDuration: `${index + 1}s`,
                h30Text: item.Title ?? "",
                p0Text: renderHtmlValue(item.Description),
              }),
            ),
            whyChooseSectionChooseNineRightFadeInUps: rightItems.map(
              (item, index) => ({
                key: item.Id,
                dataWowDuration: `${index + 1}s`,
                h30Text: item.Title ?? "",
                p0Text: renderHtmlValue(item.Description),
              }),
            ),
            whyChooseSection466s: stepItems.map((item, index) => ({
              key: item.Id,
              dataWowDuration: `${index + 1}s`,
              src:
                getItemImageUrl(item) ??
                "/Templates/Doccure/img/icons/step-icon-1.png",
              h30Text: item.Title ?? "",
              p0Text: renderHtmlValue(item.Description),
              span0Text: item.Subtitle ?? `Step ${index + 1}`,
              imageWithBasePath0Src:
                "/Templates/Doccure/img/bg/choose-step-bg.png",
              alt: getItemImageAlt(item, "choose"),
              imageWithBasePath1Alt: getItemImageAlt(item, "choose"),
            })),
          },
        )}
      />
    );
  },
  [WhyAppearance.Home18.key]: (props) => {
    const items = getListItems(props);

    return (
      <HomeEighteenWhyChooseSectionGenerated
        {...buildGeneratedSectionProps<HomeEighteenWhyChooseSectionGeneratedProps>(
          "HomeEighteenWhyChooseSection",
          {
            sectionDiv0Div0Div0DivText: props.setting.Subtitle ?? undefined,
            sectionDiv0Div0H20H2Text: props.setting.Title ?? undefined,
            sectionDiv0Div0H20H2Text2: props.setting.Description ?? undefined,
            sectionDiv0Div0H20Span0ImageWithBasePath0ImageSrc:
              getSectionImageUrl(props) ?? undefined,
            sectionDiv0Div0H20Span0ImageWithBasePath0ImageAlt:
              getSectionImageAlt(props, "why"),
            whyChooseSection336612s: items.map((item, index) => ({
              key: item.Id,
              dataWowDuration: `${1 + index * 0.5}s`,
              src:
                getItemImageUrl(item) ??
                "/Templates/Doccure/img/category/choose-icon-01.svg",
              h30Text: item.Title ?? "",
              span0Text: item.Subtitle ?? "",
              p0Text: renderHtmlValue(item.Description),
              imageWithBasePath0Src:
                getItemImageUrl(item) ??
                "/Templates/Doccure/img/category/choose-thumb-1.jpg",
              imageWithBasePath1Src:
                getItemImageUrl(item) ??
                "/Templates/Doccure/img/category/choose-large-1.jpg",
              alt: getItemImageAlt(item, "choose"),
              imageWithBasePath0Alt: getItemImageAlt(item, "thumb"),
              imageWithBasePath1Alt: getItemImageAlt(item, "thumb"),
            })),
            whyChooseSectionServicesSlides: items.map((item) => ({
              key: item.Id,
              h30Text: item.Title ?? "",
            })),
            whyChooseSectionServicesSlides2: [...items]
              .reverse()
              .map((item) => ({
                key: item.Id,
                h30Text: item.Title ?? "",
              })),
          },
        )}
      />
    );
  },
};

const GeneratedWhySection: FC<WhySectionProps> = (props) => {
  return renderGeneratedSection(
    props,
    generatedWhyRenderers,
    WhyAppearance.Home6.key,
    (sectionProps) => sectionProps.setting.Appearance?.Key,
  );
};

export default GeneratedWhySection;
